import { createSignal, For, Show, onCleanup } from 'solid-js';

// DslHelp — a draggable, always-on-top, closable reference palette for the
// Komponen Gaji DSL. Deliberately NOT a backdrop modal: it floats over the
// editor (no dimming, page stays interactive) so the user can read the syntax
// WHILE typing a formula. Drag it by the title bar; close with ×.
//
// The function list is NOT hard-coded here — it is rendered from the live
// `/api/formula/builtins` metadata (passed in via props.builtins), the same
// source the editor's autocomplete uses, so it can never drift from the engine.
// Only the fixed grammar (line forms, operators, literals) is authored, because
// that is defined in the parser/grammar, not the function registry.

// Line forms — authoritative per internal/engine/schemedoc.go.
const LINES = [
  ['# komentar', 'Catatan; diabaikan mesin sampai akhir baris.'],
  ['calc Nama = rumus', 'Nilai kerja sementara — tidak tampil di slip.'],
  ['slip(pendapatan) Nama = rumus', 'Baris pendapatan (uang masuk) di slip.'],
  ['slip(potongan) Nama = rumus', 'Baris potongan (uang keluar) di slip.'],
  ['slip(pendapatan, skiptotal) …', 'Tampil di slip tetapi TIDAK dihitung ke Total Pendapatan/Potongan (mis. BPJS perusahaan).'],
  ['slip(info) Nama = rumus', 'Baris informasi (bukan uang) di slip.'],
  ['slip(info) Nama "Label" = …', 'Label dalam kutip (opsional) jadi judul di slip.'],
  ['Nama = input(angka)', 'Diisi per karyawan saat proses gaji.'],
  ['Nama = input(teks)', 'Input berupa teks per karyawan.'],
  ['Nama = input(global angka)', 'Satu nilai untuk SELURUH karyawan dalam satu run.'],
  ['input(teks: "A" | "B")', 'Teks dibatasi ke pilihan — tampil sebagai dropdown.'],
  ['input(angka) saldo "Pinjaman"', 'Nilai diposting ke saldo saat finalisasi run.'],
  ['slip(...) … showif(Nilai > 0)', 'Baris slip hanya tampil jika kondisi benar (nilai tetap dihitung ke total).'],
  ['@Komisi calc …', 'Batasi baris ke Tipe Penggajian tertentu (tanpa @ = semua tipe).'],
  ['@Bulanan,Komisi slip(…) …', 'Beberapa tipe dipisah koma.'],
];

// Operators — authoritative per internal/engine/parser.go (infixPower).
const OPS = [
  ['+  -  *  /', 'Aritmetika. Negatif: -x.'],
  ['==  !=  <  <=  >  >=', 'Perbandingan. TIDAK bisa dirantai: pakai (a < b) and (b < c).'],
  ['and    or', 'Logika. Cabang if() yang tak terpilih tidak dihitung.'],
];

// Value literals — authoritative per parser.go parsePrimary.
const VALUES = [
  ['1000    1.5', 'Angka & desimal (pakai titik).'],
  ['5%', 'Persen — sama dengan 0,05.'],
  ['"teks"', 'Teks dalam tanda kutip ganda.'],
  ['[1000, 1500, 2000]', 'Daftar nilai — dipakai oleh brackets().'],
  ['NamaKomponen', 'Tulis nama komponen sebelumnya untuk memakai hasilnya.'],
];

const EXAMPLE = `# rumus dihitung berurutan dari atas ke bawah
calc GajiPokokHarian        = var("Gaji Pokok") / 26
slip(pendapatan) GajiPokok  = var("Gaji Pokok")
slip(pendapatan) Lembur     = TotalLemburJam1 * NilaiLembur
slip(potongan) BPJS         = round_to(var("Gaji Pokok") * 1%, 100)
# skiptotal: tampil di slip, tidak dihitung ke Total Pendapatan/Potongan
slip(pendapatan, skiptotal) BPJSKesPerusahaan  = var("BPJS Kes Perusahaan")
slip(potongan, skiptotal) PotBPJSKesPerusahaan = BPJSKesPerusahaan
slip(info) Ket "Keterangan" = concat(rupiah(NilaiLembur), " x ",
                                     text(TotalLemburJam1), " jam") showif(Lembur > 0)`;

export default function DslHelp(props) {
  const w = typeof window !== 'undefined' ? window.innerWidth : 1200;
  const [pos, setPos] = createSignal({ x: Math.max(16, w - 500), y: 84 });

  let drag = null;
  const onMove = (e) => {
    if (!drag) return;
    const x = Math.max(0, Math.min(e.clientX - drag.dx, window.innerWidth - 120));
    const y = Math.max(0, Math.min(e.clientY - drag.dy, window.innerHeight - 40));
    setPos({ x, y });
  };
  const stop = () => {
    drag = null;
    window.removeEventListener('mousemove', onMove);
    window.removeEventListener('mouseup', stop);
  };
  const onDown = (e) => {
    if (e.target.closest('.dslhelp-close')) return; // let the × handle its click
    drag = { dx: e.clientX - pos().x, dy: e.clientY - pos().y };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', stop);
    e.preventDefault(); // don't select text while dragging
  };
  onCleanup(stop);

  return (
    <div class="dslhelp" style={{ left: `${pos().x}px`, top: `${pos().y}px` }}>
      <div class="dslhelp-head" onMouseDown={onDown}>
        <span class="dslhelp-title">Sintaks &amp; fungsi Komponen Gaji</span>
        <button class="dslhelp-close" title="Tutup" onClick={() => props.onClose?.()}>×</button>
      </div>
      <div class="dslhelp-body">
        <section>
          <h5>Bentuk baris</h5>
          <For each={LINES}>{([code, desc]) =>
            <div class="dslhelp-row"><code>{code}</code><span>{desc}</span></div>
          }</For>
        </section>

        <section>
          <h5>Operator</h5>
          <For each={OPS}>{([code, desc]) =>
            <div class="dslhelp-row"><code>{code}</code><span>{desc}</span></div>
          }</For>
          <p class="dslhelp-note">
            Urutan: <code>or</code> &lt; <code>and</code> &lt; perbandingan &lt; <code>+ −</code> &lt;{' '}
            <code>× ÷</code>. Pakai kurung <code>( )</code> untuk memaksa urutan.
          </p>
        </section>

        <section>
          <h5>Nilai</h5>
          <For each={VALUES}>{([code, desc]) =>
            <div class="dslhelp-row"><code>{code}</code><span>{desc}</span></div>
          }</For>
        </section>

        <section>
          <h5>Fungsi</h5>
          <For each={props.builtins?.() ?? []}>{(b) =>
            <div class="dslhelp-row"><code>{b.Sig}</code><span>{b.Doc}</span></div>
          }</For>
          <Show when={(props.builtins?.() ?? []).length === 0}>
            <p class="dslhelp-note">Memuat daftar fungsi…</p>
          </Show>
        </section>

        <section>
          <h5>Contoh</h5>
          <pre class="dslhelp-ex">{EXAMPLE}</pre>
        </section>
      </div>
    </div>
  );
}
